import { useCallback, useSyncExternalStore } from 'react';

import { SplitController } from '@ayocore/platform/client/components/Split/SplitController';

const DEFAULT_LEFT_PERCENT = 65;
const DEFAULT_MIN_PERCENT = 20;
const DEFAULT_MAX_PERCENT = 80;

export interface UseResizableColumnsOptions {
  storageKey: string;
  defaultLeftPercent?: number;
  minPercent?: number;
  maxPercent?: number;
}

export interface UseResizableColumnsReturn {
  initialLeftPercent: number;
  saveLeftPercent: (percent: number) => void;
}

const optionsToSnapshot = (options: UseResizableColumnsOptions) => ({
  defaultLeftPercent: options.defaultLeftPercent ?? DEFAULT_LEFT_PERCENT,
  minPercent: options.minPercent ?? DEFAULT_MIN_PERCENT,
  maxPercent: options.maxPercent ?? DEFAULT_MAX_PERCENT,
});

/**
 * Хук для персиста пропорции двух колонок в localStorage.
 * Использует SplitController + useSyncExternalStore (паттерн проекта).
 * Возвращает начальный процент и колбек сохранения; логика ресайза — в Split.Resizer.
 */
export function useResizableColumns(
  options: UseResizableColumnsOptions
): UseResizableColumnsReturn {
  const {
    storageKey,
    minPercent = DEFAULT_MIN_PERCENT,
    maxPercent = DEFAULT_MAX_PERCENT,
  } = options;

  const controller = SplitController.getInstance();
  const snapshotOptions = optionsToSnapshot(options);

  const initialLeftPercent = useSyncExternalStore(
    controller.subscribe,
    () => controller.getSnapshot(storageKey, snapshotOptions),
    () => controller.getSnapshot(storageKey, snapshotOptions)
  );

  const saveLeftPercent = useCallback(
    (percent: number) => {
      controller.savePercent(storageKey, percent, {
        minPercent,
        maxPercent,
      });
    },
    [storageKey, minPercent, maxPercent]
  );

  return { initialLeftPercent, saveLeftPercent };
}
